import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const LicensePlatesServicePage: React.FC = () => {
  const [selectedPlateType, setSelectedPlateType] = useState<string>('standard');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada License Plate Services Las Vegas - New, Specialty & Replacement Plates',
    description: 'Complete Nevada license plate services in Las Vegas. New plates, specialty plates, personalized plates, and replacement plates. Immediate service. Licensed DMV provider. Call 702.979.7202.',
    keywords: 'license plates service Las Vegas, Nevada plate replacement service, specialty license plates Nevada, personalized plate service Las Vegas',
    canonical: `${siteUrl}/license-plates-service`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Nevada License Plate Services',
          'Complete Nevada license plate services including new plates, specialty plates, personalized plates, and replacement plates in Las Vegas.',
          '45'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'License Plates Service', url: '/license-plates-service' }
        ])
      ]
    }
  });

  const plateTypes = {
    standard: {
      title: 'Standard Nevada Plates',
      description: 'Basic Nevada license plates for passenger vehicles',
      features: ['Standard Nevada design', 'Valid for all passenger vehicles', 'Required for vehicle registration']
    },
    specialty: {
      title: 'Specialty & Personalized Plates',
      description: 'Custom plates with personalized messages or specialty designs',
      features: ['Custom text options', 'Specialty organization plates', 'Unique design choices']
    },
    disabled: {
      title: 'Disabled Parking Plates',
      description: 'Special plates for individuals with disabilities',
      features: ['Permanent disability designation', 'Parking privileges included', 'Medical documentation required']
    },
    replacement: {
      title: 'Replacement Plates',
      description: 'Replace lost, stolen, or damaged license plates',
      features: ['Same registration number', 'Immediate replacement', 'Duplicate plate service']
    }
  };

  const services = [
    {
      icon: 'ri-add-circle-line',
      title: 'New License Plates',
      description: 'Get new Nevada license plates for newly registered vehicles or first-time Nevada residents.'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Plate Replacement',
      description: 'Replace lost, stolen, or damaged license plates with same registration number.'
    },
    {
      icon: 'ri-star-line',
      title: 'Specialty Plates',
      description: 'Choose from various specialty plate designs supporting Nevada organizations and causes.'
    },
    {
      icon: 'ri-edit-line',
      title: 'Personalized Plates',
      description: 'Create custom license plates with your personalized message (subject to approval).'
    }
  ];

  const plateRequirements = [
    'Current vehicle registration',
    'Valid Nevada driver license',
    'Proof of vehicle insurance',
    'Completed license plate application',
    'Previous plates (for replacement)',
    'Medical documentation (for disabled plates)'
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Nevada License Plate Services
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Complete license plate services including new plates, replacements, specialty plates, and personalized 
                options. Fast, professional service for all your Nevada license plate needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Complete License Plate Solutions
              </h2>
              <p className="text-lg text-gray-600">
                From standard plates to custom designs, we handle all Nevada license plate services
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${service.icon} text-2xl text-purple-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Plate Types */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nevada License Plate Options
              </h2>
              <p className="text-lg text-gray-600">
                Choose the right license plate type for your needs
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Plate Type Selector */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {Object.entries(plateTypes).map(([key, plate]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPlateType(key)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedPlateType === key 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {plate.title}
                  </button>
                ))}
              </div>

              {/* Plate Type Details */}
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {plateTypes[selectedPlateType as keyof typeof plateTypes].title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {plateTypes[selectedPlateType as keyof typeof plateTypes].description}
                </p>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Features:</h4>
                  <div className="space-y-2">
                    {plateTypes[selectedPlateType as keyof typeof plateTypes].features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-xs text-purple-600"></i>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What You'll Need
                </h2>
                <p className="text-gray-600 mb-8">
                  Gather these documents before your appointment to ensure smooth processing of your license plate request.
                </p>
                <div className="space-y-4">
                  {plateRequirements.map((req, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-sm text-purple-600"></i>
                      </div>
                      <span className="text-gray-800">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Specialty Plate Information
                </h3>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Nevada offers numerous specialty license plates supporting various organizations, 
                    causes, and interests. Popular options include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Military and veteran plates</li>
                    <li>University and college plates</li>
                    <li>Conservation and environmental plates</li>
                    <li>Professional organization plates</li>
                    <li>Charitable cause plates</li>
                  </ul>
                  <p className="text-sm text-purple-600 font-medium">
                    Additional fees may apply for specialty and personalized plates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                License Plate Service Process
              </h2>
              <p className="text-lg text-gray-600">
                Our efficient process gets you on the road with new plates quickly
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {[
                  {
                    step: 1,
                    title: 'Choose Plate Type',
                    description: 'Select from standard, specialty, or personalized license plate options based on your needs.',
                    time: '5 minutes'
                  },
                  {
                    step: 2,
                    title: 'Document Verification',
                    description: 'We verify your registration, insurance, and identification documents.',
                    time: '10 minutes'
                  },
                  {
                    step: 3,
                    title: 'Application Processing',
                    description: 'Complete the Nevada DMV application and process payment for your new plates.',
                    time: '15 minutes'
                  },
                  {
                    step: 4,
                    title: 'Plate Installation',
                    description: 'Receive your new license plates and any necessary registration updates.',
                    time: '5 minutes'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-6">
                    <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                        <span className="text-sm font-medium text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
                          {item.time}
                        </span>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                License Plate Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How long does it take to get new license plates?",
                  answer: "Standard license plates are available immediately. Specialty and personalized plates may take 4-6 weeks for manufacturing after approval."
                },
                {
                  question: "Can I keep my current plate number when replacing plates?",
                  answer: "Yes, replacement plates will have the same number as your original plates. You'll need to bring your current registration and ID."
                },
                {
                  question: "What if my personalized plate message is rejected?",
                  answer: "Nevada DMV reviews all personalized plate requests for appropriateness. If rejected, we'll help you choose an alternative message that meets guidelines."
                },
                {
                  question: "Do I need to surrender my old plates?",
                  answer: "When getting replacement plates, you should bring your old plates if available. For specialty upgrades, old plates are typically retained by DMV."
                },
                {
                  question: "Are there restrictions on personalized plate messages?",
                  answer: "Yes, personalized plates cannot contain profanity, offensive language, or messages that could be misleading. Messages are limited to 7 characters including spaces."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-purple-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready for New License Plates?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/location" 
                 className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Visit Our Location
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>Speedy DMV Services - 4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LicensePlatesServicePage;
